import { Detector } from "scripts/classes/detector";
import { StateType } from "../app";
import { MessageToast } from "../ui/components/toast";

export const CRITICAL_VALUE = 'criticalValue';
export const CONE_WIDTH = 'coneWidth';
export const START_ANGLE = 'startAngle';
export const END_ANGLE = 'endAngle';
export const STEP_RAY = 'stepRay';
export const SHOW_RAY_PROFILE = 'showRayProfile';

export type SectorDb = {
        id: string;
        name: string;
        created_at: Date;
        updated_at: Date;
}

export type SectorPushDb = {
    folderId: string
    name: string
    startLng: number
    startLat: number
    azimuth: number
    distance: number
    startAngle: number
    endAngle: number
    stepRay: number
    coneWidth: number
    criticalValue: number
}

/**
 * Сохранение сектора, с привязкой к папке
 * @param {*} folderId 
 * @param {*} data - объект с данными точки, включая name
 * @returns 
 */
export async function apiSaveSector(name: string, data: StateType): Promise<Detector | null> {
    try {
        // Проверка на выбранную папку в dropdown
        if (!data.folder) {
            MessageToast.showDanger('Папка не выбрана');
            return null;
        }

        // Проверяем наличие необходимых данных
        if (!data.detector || !data.detector.lat || !data.detector.lng || !data.detector.azimuth || !data.detector.coneWidth) {
            MessageToast.showDanger('Недостаточно данных для создания сектора');
            return null;
        }

        const requestBody = {
            folderId: data.folder.id,
            name: name || "", // Название точки (обязательное!)
            lng: data.detector.lng,
            lat: data.detector.lat,
            azimuth: data.detector.azimuth,
            distance: data.detector.distance,
            startAngle: parseFloat(localStorage.getItem(START_ANGLE) ?? '0'),
            endAngle: parseFloat(localStorage.getItem(END_ANGLE) ?? '30'),
            stepRay: parseFloat(localStorage.getItem(STEP_RAY) ?? '0.5'),
            coneWidth: data.detector.coneWidth || 30,
            criticalValue: parseFloat(localStorage.getItem(CRITICAL_VALUE) ?? '800'),
        };

        console.log('Сохранение сектора на сервере:', requestBody);

        const response = await fetch(`/sector/`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`Ошибка ${response.status}:`, errorText);
            return null;
        }

        const r = await response.json();
        const responseDetector = new Detector(r.id, r.lat, r.lng, r.distance, r.coneWidth, false);
        responseDetector.azimuth = r.azimuth;
        return responseDetector;

    } catch (err) {
        console.error('Запрос не удался', err);
        return null;
    }
}


/**
 * Обновление данных сектора
 */
export async function apiUpdateSector(detector: Detector): Promise<Detector | null> {
    try {

        // Проверяем наличие необходимых данных
        if (!detector || !detector.lat || !detector.lng || !detector.azimuth || !detector.coneWidth) {
            MessageToast.showDanger('Недостаточно данных для создания сектора');
            return null;
        }

        const requestBody = {
            id: detector.id,
            name: detector.name, // Название точки (обязательное!)
            lng: detector.lng,
            lat: detector.lat,
            azimuth: detector.azimuth,
            distance: detector.distance,
            startAngle: parseFloat(localStorage.getItem(START_ANGLE) ?? '0'),
            endAngle: parseFloat(localStorage.getItem(END_ANGLE) ?? '30'),
            stepRay: parseFloat(localStorage.getItem(STEP_RAY) ?? '0.5'),
            coneWidth: detector.coneWidth || 30,
            criticalValue: parseFloat(localStorage.getItem(CRITICAL_VALUE) ?? '800'),
        };

        const response = await fetch(`/sector/${detector.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`Ошибка обновления ${response.status}:`, errorText);
            return null;
        }

        const r = await response.json();
        const responseDetector = new Detector(r.id, r.lat, r.lng, r.distance, r.coneWidth, false);
        responseDetector.azimuth = r.azimuth;

        return responseDetector;
    } catch (err) {
        console.error('Ошибка при обновлении сектора', err);
        return null;
    }
}


/**
 * Получение секторов из папки
 * @param {*} folderId 
 * @returns - возвращает массив секторов или пустой массив если их нет
 */
export async function apiFetchSectorsForFolder(folderId: string) {
    try {
        const response = await fetch(`/sector?folderId=${folderId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        // Если 404 - в папке просто нет секторов, это нормально
        if (response.status === 404) {
            console.log('В папке нет секторов');
            return [];
        }

        if (!response.ok) {
            console.error(`Ошибка ${response.status}`);
            return [];
        }

        const data = await response.json();
        
        // Убеждаемся что возвращаем массив
        if (!Array.isArray(data)) {
            console.warn('Сервер вернул не массив:', data);
            return [];
        }

        return data;
    } catch (err) {
        console.error('Запрос не удался', err);
        return [];
    }
}


/**
 * Получение конкретного сектора по ID
 */
export async function apiGetSectorById(sectorId: string) {
    try {
        const response = await fetch(`/sector?id=${sectorId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            console.error(`Ошибка при получении сектора: ${response.status}`);
            return null;
        }

        return await response.json();
    } catch (err) {
        console.error('Запрос не удался', err);
        return null;
    }
}

/**
 * Удаление сектора
 * @param {string|number} sectorId 
 */
export async function apiDeleteSector(sectorId: string) {
    try {
        const response = await fetch(`/sector/${sectorId}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            console.error(`Не удалось удалить сектор: ${response.status}`);
            return false;
        }

        console.log(`Сектор ${sectorId} успешно удален`);
        return true;
    } catch (err) {
        console.error('Ошибка при удалении', err);
        return false;
    }
}
